import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { isSignedIn, SIGN_IN_PATH } from "@/lib/auth";
import { emailProblem } from "@/lib/validation";
import {
  useForgotPasswordMutation,
  useResendResetCodeMutation,
  useResetPasswordMutation,
  type OtpChallenge,
} from "../auth-api";
import {
  authErrorMessage,
  PASSWORD_RULE,
  passwordProblem,
  retryAfterSeconds,
  useCountdown,
} from "../auth-messages";
import {
  AuthHeading,
  AuthLayout,
  CodeField,
  EmailField,
  FormAlert,
  PasswordField,
  ResendButton,
  SubmitButton,
} from "./AuthParts";

/**
 * Forgot password: an emailed code, then a new password.
 *
 * Also the way in for an account migrated from Cognito, which has never had a
 * password here. This is the one flow that still uses an emailed code. The reset does *not* sign anyone in — it ends every session
 * the account had — so success lands on the sign-in form with a notice.
 *
 * The first step answers the same whether or not the address is an admin, and
 * so does this screen: "if that address has an account, a code is on its way".
 */
export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  if (isSignedIn()) return <Navigate to="/" replace />;

  return (
    <AuthLayout>
      <ForgotPasswordForm initialEmail={params.get("email") ?? ""} />
    </AuthLayout>
  );
}

function ForgotPasswordForm({ initialEmail }: { initialEmail: string }) {
  const navigate = useNavigate();
  const [forgot, forgotState] = useForgotPasswordMutation();
  const [reset, resetState] = useResetPasswordMutation();
  const [resend, resendState] = useResendResetCodeMutation();

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [resendIn, startCooldown] = useCountdown();

  const accept = (sent: OtpChallenge) => {
    setChallenge(sent);
    startCooldown(sent.resend_available_in);
    setCode(sent.dev_code ?? "");
  };

  const requestCode = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      accept(await forgot({ email: email.trim() }).unwrap());
    } catch (err) {
      setError(authErrorMessage(err, "email"));
    }
  };

  const resendCode = async () => {
    setError(null);
    try {
      accept(await resend({ email: email.trim() }).unwrap());
    } catch (err) {
      setError(authErrorMessage(err, "code"));
      const wait = retryAfterSeconds(err);
      if (wait) startCooldown(wait);
    }
  };

  const problem = password === "" ? null : passwordProblem(password);
  const mismatch = confirm !== "" && confirm !== password;

  const submitReset = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      await reset({ email: email.trim(), code, new_password: password }).unwrap();
      void navigate(`${SIGN_IN_PATH}?reset=1&email=${encodeURIComponent(email.trim())}`, {
        replace: true,
      });
    } catch (err) {
      setError(authErrorMessage(err, "code"));
    }
  };

  if (challenge) {
    return (
      <>
        <AuthHeading title="Choose a new password">
          If <span className="text-content font-medium">{challenge.email}</span> has an admin
          account, a reset code is on its way. It expires in {Math.round(challenge.expires_in / 60)}{" "}
          minutes.
        </AuthHeading>

        <form onSubmit={(event) => void submitReset(event)} noValidate className="space-y-4">
          <CodeField label="Reset code" value={code} onChange={setCode} autoFocus />

          <PasswordField
            label="New password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            hint={PASSWORD_RULE}
            error={problem ?? undefined}
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            error={mismatch ? "The two passwords don't match." : undefined}
          />

          <FormAlert error={error} />

          <SubmitButton
            busy={resetState.isLoading}
            busyLabel="Saving…"
            disabled={
              code.length !== 6 || passwordProblem(password) !== null || confirm !== password
            }
          >
            Set password
          </SubmitButton>
        </form>

        <p className="text-content-subtle mt-3 text-xs leading-relaxed">
          Every browser signed in to this account is signed out when the password changes.
        </p>

        <div className="mt-4 flex items-center justify-between text-xs">
          <Link
            to={SIGN_IN_PATH}
            className="text-content-muted hover:text-content transition-colors"
          >
            Back to sign in
          </Link>
          <ResendButton
            secondsLeft={resendIn}
            busy={resendState.isLoading}
            onClick={() => void resendCode()}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <AuthHeading title="Reset your password">
        Enter your admin email and we'll send a code to set a new password.
      </AuthHeading>

      <form onSubmit={(event) => void requestCode(event)} noValidate className="space-y-4">
        <EmailField value={email} onChange={setEmail} autoFocus />

        <FormAlert error={error} />

        <SubmitButton
          busy={forgotState.isLoading}
          busyLabel="Sending…"
          disabled={emailProblem(email) !== null}
        >
          Send code
        </SubmitButton>
      </form>

      <p className="mt-4 text-xs">
        <Link to={SIGN_IN_PATH} className="text-content-muted hover:text-content transition-colors">
          Back to sign in
        </Link>
      </p>
    </>
  );
}
